import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, InputLabel, MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { ALPHABET_REGEX } from '../../../constants/regex.ts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import Upload from '../../common/Upload.tsx';
import { useDebounce } from 'use-debounce';
import { DELAY_BEFORE_SEARCH } from '../../../constants/numbers.ts';

export default function NewPackage() {
	const { t } = useTranslation();
	const { id } = useParams();
	const [category, setCategory] = useState<any[]>([]);
	const [media, setMedia] = useState<any[]>([]);
	const [contentType, setContentType] = useState<any[]>([]);
	const [quality, setQuality] = useState<any[]>([]);
	const [group, setGroup] = useState<any[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [valMedia, setValMedia] = useState<any | null>(null);
	const [valCategory, setValCategory] = useState<any | null>(null);
	const [valQuality, setValQuality] = useState<any | null>(null);
	const [valContentType, setValContentType] = useState<any | null>(null);
	const [valGroup, setValGroup] = useState<any | null>(null);
	const [search] = useDebounce(inputValue, DELAY_BEFORE_SEARCH);
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(false);
	const [files, setFiles] = useState<(File | string)[]>([]);

	const NewPackageSchema = z.object({
		title: z
			.string()
			.min(3, t('invalid_min_name_message'))
			.regex(ALPHABET_REGEX, { message: t('invalid_name_message') }),
		price: z.coerce.number(),
		requirement_title: z.string().min(3, { message: t('requirement_title_must_be_at_least_3_characters') }),
		requirement_description: z.string().min(3, { message: t('requirement_description_must_be_at_least_3_characters') }),
		rules: z.string().min(3, { message: t('rules_must_be_at_least_3_characters') }),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm<z.infer<typeof NewPackageSchema>>({
		resolver: zodResolver(NewPackageSchema),
	});
	useEffect(() => {
		(async () => {
			setLoading(true);
			axios({
				url: `admin/options/studio_media`,
			}).then(res => {
				if (res.data.success) {
					setMedia(res.data.data);
				}
			});
			axios({
				url: `admin/options/studio_category`,
			}).then(res => {
				if (res.data.success) {
					setCategory(res.data.data);
				}
			});
			axios({
				url: `admin/options/studio_quality`,
			}).then(res => {
				if (res.data.success) {
					setQuality(res.data.data);
				}
			});
			axios({
				url: `admin/options/studio_content_type`,
			}).then(res => {
				if (res.data.success) {
					setContentType(res.data.data);
				}
			});
			axios({
				url: `admin/options/studio_group`,
			}).then(res => {
				if (res.data.success) {
					setGroup(res.data.data);
				}
			});
			setLoading(false);
		})();
	}, [search]);

	const handleSaveInfo = (data: any) => {
		const formData = new FormData();
		formData.append('title', data.title);
		formData.append('category', valCategory.id);
		formData.append('media', valMedia.id);
		formData.append('content_type', valContentType.id);
		formData.append('quality', valQuality.id);
		formData.append('price', data.price);
		formData.append('group', valGroup.id);
		formData.append('requirement_title', data.requirement_title);
		formData.append('requirement_description', data.requirement_description);
		formData.append('rules', data.rules);
		files.forEach(file => {
			if (file instanceof File) {
				formData.append('image', file);
			}
		});
		if (id) {
			formData.append('_method', 'PUT');
		}
		axios({
			url: id ? `admin/studio-packages/${id}` : 'admin/studio-packages',
			method: 'POST',
			data: formData,
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(res => {
			if (res.data.success) {
				navigate('/admin/studio/package');
			}
		});
	};

	useEffect(() => {
		{
			if (id) {
				axios({
					url: `admin/studio-packages/${id}`,
				}).then(res => {
					if (res.data.success) {
						setValue('title', res.data.data.title);
						setValMedia({ id: res.data.data.media, title: res.data.data.media_title });
						setValCategory({ id: res.data.data.category, title: res.data.data.category_title });
						setValQuality({ id: res.data.data.quality, title: res.data.data.quality_title });
						setValContentType({ id: res.data.data.content_type, title: res.data.data.content_title });
						setValue('price', res.data.data.price);
						setValue('requirement_title', res.data.data.requirement_title);
						setValue('requirement_description', res.data.data.requirement_description);
						setValGroup({ id: res.data.data.group, title: res.data.data.group_title });
						setValue('rules', res.data.data.rules);
						setFiles([res.data.data.image]);
					}
				});
			}
		}
	}, []);

	const handleCancel = () => {
		reset();
	};

	const handleSearchMedia = (_: any, newValue: any) => {
		setValMedia(newValue);
	};
	const handleSearchCategory = (_: any, newValue: any) => {
		setValCategory(newValue);
	};
	const handleSearchQuality = (_: any, newValue: any) => {
		setValQuality(newValue);
	};
	const handleSearchContentType = (_: any, newValue: any) => {
		setValContentType(newValue);
	};
	const handleSearchGroup = (_: any, newValue: any) => {
		setValGroup(newValue);
	};
	return (
		<Box className="w-full">
			{loading ? (
				<Box className="flex min-h-96 w-full items-center justify-center">
					<CircularProgress />
				</Box>
			) : (
				<form className="mx-auto flex w-full flex-col gap-6 rounded-lg bg-white p-5" onSubmit={handleSubmit(handleSaveInfo)}>
					<Grid container spacing={3}>
						<Grid xs={12}>
							<InputLabel className="float-start flex px-2 pb-2">{t('title')}</InputLabel>
							<TextField variant="outlined" fullWidth size="small" {...register('title')} error={!!errors.title} helperText={errors.title?.message} inputProps={{ maxLength: 50 }} />
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start flex px-2 pb-2">{t('release-platform')}</InputLabel>
							<Autocomplete
								getOptionLabel={media => (typeof media === 'string' ? media : media.title)}
								filterOptions={x => x}
								options={media}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={valMedia}
								onChange={handleSearchMedia}
								noOptionsText={t('no_media')}
								onInputChange={(_, newInputValue) => {
									setInputValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={valMedia}>
											{option.title}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField {...params} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start flex px-2 pb-2">{t('category')}</InputLabel>
							<Autocomplete
								getOptionLabel={category => (typeof category === 'string' ? category : category.title)}
								filterOptions={x => x}
								options={category}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={valCategory}
								onChange={handleSearchCategory}
								noOptionsText={t('no_category')}
								onInputChange={(_, newInputValue) => {
									setInputValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={valCategory}>
											{option.title}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField {...params} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start flex px-2 pb-2">{t('quality')}</InputLabel>
							<Autocomplete
								getOptionLabel={quality => (typeof quality === 'string' ? quality : quality.title)}
								filterOptions={x => x}
								options={quality}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={valQuality}
								onChange={handleSearchQuality}
								noOptionsText={t('no_quality')}
								onInputChange={(_, newInputValue) => {
									setInputValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={valQuality}>
											{option.title}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField {...params} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start flex px-2 pb-2">{t('content_type')}</InputLabel>
							<Autocomplete
								getOptionLabel={contentType => (typeof contentType === 'string' ? contentType : contentType.title)}
								filterOptions={x => x}
								options={contentType}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={valContentType}
								onChange={handleSearchContentType}
								noOptionsText={t('no_contentType')}
								onInputChange={(_, newInputValue) => {
									setInputValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={valContentType}>
											{option.title}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField {...params} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start px-2 pb-2">{t('group')}</InputLabel>
							<Autocomplete
								className={'w-full'}
								getOptionLabel={contentType => (typeof contentType === 'string' ? contentType : contentType.title)}
								filterOptions={x => x}
								options={group}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={valGroup}
								onChange={handleSearchGroup}
								noOptionsText={t('no_group')}
								onInputChange={(_, newInputValue) => {
									setInputValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={valContentType}>
											{option.title}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField {...params} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<InputLabel className="float-start flex px-2 pb-2">{t('price')}</InputLabel>
							<TextField variant="outlined" fullWidth size="small" {...register('price')} error={!!errors.price} helperText={errors.price?.message} inputProps={{ maxLength: 50 }} />
						</Grid>
						<Grid xs={12} md={12}>
							<InputLabel className="float-start flex px-2 pb-2">{t('requirement_title')}</InputLabel>
							<TextField variant="outlined" fullWidth size="small" {...register('requirement_title')} error={!!errors.requirement_title} helperText={errors.requirement_title?.message} inputProps={{ maxLength: 50 }} />
						</Grid>
						<Grid xs={12} md={12}>
							<InputLabel className="float-start flex px-2 pb-2">{t('requirement_description')}</InputLabel>
							<TextField variant="outlined" multiline rows={4} fullWidth size="small" {...register('requirement_description')} error={!!errors.requirement_description} helperText={errors.requirement_description?.message} inputProps={{ maxLength: 300 }} />
						</Grid>
						<Grid xs={12} md={12}>
							<InputLabel className="float-start flex px-2 pb-2">{t('rules_and_regulations')}</InputLabel>
							<TextField variant="outlined" multiline rows={4} {...register('rules')} error={!!errors.rules} helperText={errors.rules?.message} fullWidth size="small" inputProps={{ maxLength: 50 }} />
						</Grid>
						<Upload type={'media'} files={files} className="mx-3 w-full" setFiles={setFiles} text={t('upload_image_video_image')} multiple />
						<Grid xs={12} className="flex flex-row justify-end gap-4">
							<Button variant="outlined" className="my-2 w-full border-gray-400 text-text-secondary md:w-20" onClick={handleCancel}>
								{t('cancel')}
							</Button>
							<Button variant="contained" className="my-2 w-full border-gray-400 text-white md:w-28" type="submit">
								{t('register')}
							</Button>
						</Grid>
					</Grid>
				</form>
			)}
		</Box>
	);
}
